'use client';

import { formatRoePercent, roePercent } from '@outpoll/utils';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import { ChevronRight } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import type { BestQuote, FuturesPair, PositionDto } from '@/lib/api/futures/client';
import { useFuturesPairs, useOpenFuturesPositionsList } from '@/lib/api/futures/hooks';
import type { Coin } from '@/lib/api/transaction/models';
import {
  fmtSignedAmount,
  liveUnrealizedPnl,
  resolveContract,
  sideClass,
} from '@/lib/components/Profile/PositionTables/futures/format';
import { ZoneLink } from '@/lib/components/ZoneLink';
import { useFuturesBestQuoteWebSocket } from '@/lib/hooks/useFuturesBestQuoteWebSocket';
import { useCoins } from '@/lib/providers/CoinsProvider';
import { TokenIcon } from '@/lib/ui-kit/token-icon';
import { cn } from '@/lib/ui-kit/utils';
import { CHANGE_DIRECTION_TEXT, changeDirection } from '@/lib/utils/pairFormat';
import { MarketListSkeleton } from './FuturesPairs';
import { MARKET_LIST_CLASS, MARKET_ROW_CLASS, PAIR_ICON_SIZE } from './navStyles';
import { FUTURES_POSITIONS_PATH } from './routes';

interface PositionRowProps {
  position: PositionDto;
  quote: BestQuote | undefined;
  pairs: FuturesPair[] | undefined;
  coins: Coin[];
}

interface PositionListProps {
  positions: PositionDto[] | undefined;
  isLoading: boolean;
}

// ── Parts ──

/** Laid out like the pair list's row: uPnL where the price is, ROE where the day's change is. */
function PositionRow({ position, quote, pairs, coins }: PositionRowProps) {
  const t = useTranslations('nav.menu.futuresPositions');
  const contract = resolveContract(pairs, coins, position.pi, '');
  const pnl = liveUnrealizedPnl(position, quote, contract);
  const roe = roePercent(pnl, position.im);
  const sideLabel = position.s === 'LONG' ? t('long') : t('short');

  return (
    <li>
      <NavigationMenu.Link asChild>
        <ZoneLink href={contract.path} className={MARKET_ROW_CLASS}>
          <TokenIcon
            symbol={contract.pair?.baseCurrency ?? contract.symbol}
            iconUrl={contract.pair?.icon}
            size={PAIR_ICON_SIZE}
            fallback="initials"
            className="shrink-0 rounded-full"
          />
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="truncate text-[14px] font-medium leading-5 text-v2-text-primary">
              {contract.symbol}
            </span>
            <span className="text-[12px] leading-[17px] text-v2-text-secondary">
              <span className={sideClass(position.s)}>{sideLabel}</span> {position.l}x
            </span>
          </span>
          <span className="flex shrink-0 flex-col items-end gap-0.5 tabular-nums">
            <span className="text-[14px] font-medium leading-5 text-v2-text-primary">
              {fmtSignedAmount(pnl, contract)}
            </span>
            <span
              className={cn(
                'text-[12px] leading-[17px]',
                CHANGE_DIRECTION_TEXT[changeDirection(roe)],
              )}
            >
              {formatRoePercent(roe)}
            </span>
          </span>
        </ZoneLink>
      </NavigationMenu.Link>
    </li>
  );
}

function PositionList({ positions, isLoading }: PositionListProps) {
  const t = useTranslations('nav.menu.futuresPositions');
  const { data: pairs } = useFuturesPairs();
  const { coins } = useCoins();
  const pairIds = useMemo(
    () => [...new Set(positions?.map((position) => position.pi))],
    [positions],
  );
  const quotes = useFuturesBestQuoteWebSocket(pairIds);

  if (isLoading) return <MarketListSkeleton />;
  if (!positions?.length) {
    return <li className="py-6 text-center text-[13px] text-v2-text-secondary">{t('empty')}</li>;
  }
  return positions.map((position) => (
    <PositionRow
      key={position.id}
      position={position}
      quote={quotes.get(position.pi)}
      pairs={pairs}
      coins={coins}
    />
  ));
}

/** The Futures mega menu's right column while My positions is picked: the open positions, live. */
export function FuturesPositions() {
  const t = useTranslations('nav.menu.futuresPositions');
  const { data: positions, isLoading } = useOpenFuturesPositionsList();

  return (
    <div className="flex w-[300px] shrink-0 flex-col gap-2">
      {/* The search box's 40px slot, so the list lines up with the pair list's. */}
      <div className="flex h-10 items-center justify-between px-3">
        <span className="text-[13px] font-medium text-v2-text-secondary">{t('title')}</span>
        <NavigationMenu.Link asChild>
          <ZoneLink
            href={FUTURES_POSITIONS_PATH}
            className="flex items-center gap-0.5 text-[13px] font-medium text-v2-text-secondary outline-none transition-colors hover:text-v2-text-primary focus-visible:text-v2-text-primary"
          >
            {t('viewAll')}
            <ChevronRight aria-hidden className="size-4" />
          </ZoneLink>
        </NavigationMenu.Link>
      </div>
      <div className="flex h-4 items-center justify-between px-3 text-[12px] leading-4 text-v2-text-secondary">
        <span>{t('name')}</span>
        <span>{t('unrealizedPnl')}</span>
      </div>
      {/* Shorter by the labels and their gap, so the panel keeps the pair list's height. */}
      <ul className={cn(MARKET_LIST_CLASS, 'h-[298px]')}>
        <PositionList positions={positions} isLoading={isLoading} />
      </ul>
    </div>
  );
}
